import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import {
    applySceneMaterial, fetchScene, saveSceneMode, Scene, SceneMode, SceneOverride,
} from './scene-api';

export interface SceneContextValue {
    taskId: number;
    // the job open in the editor: a shape of another one opens that job
    jobId: number | null;
    // where the project folder is chosen; null for a task outside a project
    projectId: number | null;
    open: boolean;
    scene: Scene | null;
    loading: boolean;
    error: string | null;
    // the job had unsaved changes when the panel opened: the server counts without them
    unsaved: boolean;
    toggle(): void;
    setMode(mode: SceneMode): Promise<void>;
    // sets an item's material in the current mode, or only of the given shapes; throws, so the caller can show why
    apply(item: string, value: string, includeExceptions: boolean, shapes?: number[]): Promise<void>;
    // opens the shape's drawing and focuses it; the editor of another job shows it there
    showShape(shape: SceneOverride): void;
    // marks the shape on the canvas while pointed at, when it is on the current drawing
    highlightShape(id: number | null): void;
}

export const SceneContext = createContext<SceneContextValue | null>(null);

export function useSceneContext(): SceneContextValue | null {
    return useContext(SceneContext);
}

const OPEN_REQUEST = 'cvat-scene-open';

// The task page's link opens the editor with the panel open. A URL parameter would not get here:
// the editor drops them while the job loads, before the panel mounts.
export function requestScenePanel(taskId: number): void {
    try {
        sessionStorage.setItem(OPEN_REQUEST, String(taskId));
    } catch {
        // storage blocked: the editor opens with the panel closed
    }
}

function takeOpenRequest(taskId: number): boolean {
    try {
        if (sessionStorage.getItem(OPEN_REQUEST) !== String(taskId)) return false;
        sessionStorage.removeItem(OPEN_REQUEST);
        return true;
    } catch {
        return false;
    }
}

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

interface Props {
    // null outside a task job: no scene, the context stays null
    taskId: number | null;
    jobId: number | null;
    projectId: number | null;
    // the job's annotations are being saved; the scene is read again when that ends
    saving: boolean;
    hasUnsavedChanges(): boolean;
    save(): Promise<void>;
    reloadAnnotations(): Promise<void>;
    showShape(shape: SceneOverride): void;
    highlightShape(id: number | null): void;
    children: React.ReactNode;
}

export function SceneProvider({
    taskId, jobId, projectId, saving, hasUnsavedChanges, save, reloadAnnotations, showShape, highlightShape, children,
}: Props): JSX.Element {
    const [open, setOpen] = useState(false);
    const [scene, setScene] = useState<Scene | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [unsaved, setUnsaved] = useState(false);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    // a slow answer must not replace the one of a later request, e.g. after a mode switch
    const latest = useRef(0);

    const load = useCallback(async (request: () => Promise<Scene>) => {
        const id = ++latest.current;
        setLoading(true);
        setError(null);
        try {
            const next = await request();
            if (mounted.current && id === latest.current) setScene(next);
        } catch (e) {
            if (mounted.current && id === latest.current) setError(messageOf(e));
        } finally {
            if (mounted.current && id === latest.current) setLoading(false);
        }
    }, []);

    // the header badge needs the scene before the panel opens
    useEffect(() => {
        if (taskId === null) return;
        load(() => fetchScene(taskId));
        if (takeOpenRequest(taskId)) setOpen(true);
    }, [taskId, load]);

    // shapes drawn in the editor reach the server, and so the scene, when the job is saved
    const wasSaving = useRef(saving);
    useEffect(() => {
        if (wasSaving.current && !saving && taskId !== null) {
            load(() => fetchScene(taskId));
            if (open) setUnsaved(hasUnsavedChanges());
        }
        wasSaving.current = saving;
    }, [saving]);

    const toggle = useCallback(() => {
        if (taskId === null) return;
        if (!open) {
            setUnsaved(hasUnsavedChanges());
            load(() => fetchScene(taskId));
        }
        setOpen(!open);
    }, [open, taskId, hasUnsavedChanges, load]);

    const setMode = useCallback(async (mode: SceneMode) => {
        if (taskId !== null) await load(() => saveSceneMode(taskId, mode));
    }, [taskId, load]);

    const apply = useCallback(async (item: string, value: string, includeExceptions: boolean, shapes?: number[]) => {
        if (taskId === null || !scene) return;
        // the server writes the shapes: edits still in the browser would be lost on the reload
        if (hasUnsavedChanges()) await save();
        const next = await applySceneMaterial(taskId, {
            mode: scene.mode, item, value, include_exceptions: includeExceptions, ...(shapes ? { shapes } : {}),
        });
        // an answer still on its way is older than this one, and will not clear loading itself
        latest.current += 1;
        if (!mounted.current) return;
        setScene(next);
        setLoading(false);
        setUnsaved(false);
        await reloadAnnotations();
    }, [taskId, scene, hasUnsavedChanges, save, reloadAnnotations]);

    const value = useMemo(() => (taskId === null ? null : {
        taskId,
        jobId,
        projectId,
        open,
        scene,
        loading,
        error,
        unsaved,
        toggle,
        setMode,
        apply,
        showShape,
        highlightShape,
    }), [
        taskId, jobId, projectId, open, scene, loading, error, unsaved,
        toggle, setMode, apply, showShape, highlightShape,
    ]);
    return <SceneContext.Provider value={value}>{children}</SceneContext.Provider>;
}
